<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <div class="d-flex align-center">
                    <h1 class="text-left">Companies</h1>
                    <v-card class="ml-6" color="surface-light" style="min-width: 300px;">
                        <v-card-text class="pa-2">
                            <v-text-field
                                v-model="quickSearch"
                                append-inner-icon="mdi-magnify"
                                autofocus
                                clearable
                                density="compact"
                                hide-details
                                label="Quick Search"
                                single-line
                                variant="solo"
                                @click:clear="()=>{quickSearch=null;currentPage=1;fetchCompanies()}"
                                @keydown.enter="applyQuickSearch"
                                @click:append-inner="applyQuickSearch"
                            ></v-text-field>
                        </v-card-text>
                    </v-card>
                </div>
                <div>
                    <v-btn color="black" prepend-icon="mdi-plus" @click="createDialogSwitch = true">Add</v-btn>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" md="3" sm="6">
                <v-select v-model="formData['portal']" :items="portals" clearable
                          item-title="name" item-value="id"
                          label="Portal" @update:model-value="()=>{currentPage=1;fetchCompanies()}"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-select v-model="formData['status']" :items="[
                    {value:'enabled',title:'Enabled'},
                    {value:'disabled',title:'Disabled'},
                    {value:'restricted',title:'Restricted'}
                    ]" clearable label="Status" @update:model-value="()=>{currentPage=1;fetchCompanies()}"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-select v-model="formData['payment_status']" :items="[
                    {value:'unpaid',title:'Defaulter'},
                    {value:'paid',title:'Non Defaulter'},
                    ]" clearable label="Payment Status" @update:model-value="()=>{currentPage=1;fetchCompanies()}"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th></th>
                        <th-btn :sorted-column="sortedColumn??null" column="companies.id"
                                sortable title="ID" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="portal_name"
                                sortable title="Portal" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="company_name"
                                sortable title="Company Name" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="account_code"
                                sortable title="Account Code" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="contact_name"
                                sortable title="Contact Name" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="email"
                                sortable title="Email" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="phone"
                                sortable title="Phone" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="status"
                                sortable title="Status" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="created_at"
                                sortable title="Created At" @sort="sort"/>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="company in companies" v-if="!loading && companies.length" :key="company.id"
                        class="text-left">
                        <td>
                            <v-avatar v-if="company.company_logo" :image="company.company_logo" size="32"/>
                        </td>
                        <td>{{ company.id }}</td>
                        <td class="text-capitalize">{{ company.portal_name }}</td>
                        <td>{{ company.company_name }}</td>
                        <td>{{ company.account_code }}</td>
                        <td>{{ company.contact_name }}</td>
                        <td>{{ company.email }}</td>
                        <td>{{ company.phone }}</td>
                        <td class="text-capitalize">{{ strOperations().capitalize(company.status) }}</td>
                        <td>{{ formatDateTime(company.created_at) }}</td>
                        <td>
                            <div class="d-flex align-center ga-1">
                                <NoteIcon entity-type="companies" :entity-id="company.id" :note-count="company.notes_count ?? 0"/>
                                <v-btn-group
                                    density="compact"
                                    divided
                                    variant="outlined"
                                >
                                    <v-btn :disabled="usersDialogSwitch" prepend-icon="mdi-account-multiple"
                                           size="x-small" @click="companyUsers(company)">Users
                                    </v-btn>
                                    <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                           size="x-small" @click="editCompany(company)">Edit
                                    </v-btn>
                                    <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                           @click="deleteCompany(company)">Delete
                                    </v-btn>
                                    <v-menu>
                                        <template v-slot:activator="{ props }">
                                            <v-btn icon="mdi-dots-vertical" v-bind="props" variant="text"></v-btn>
                                        </template>

                                        <v-list density="compact">
                                            <v-list-item
                                                density="compact"
                                                title="Switch Status"
                                                @click="changeStatus(company)"
                                            />
                                            <v-list-item
                                                v-if="isSuperAdmin"
                                                density="compact"
                                                :title="company.billing_exempt ? 'Billing Exempt (on)' : 'Billing Exempt'"
                                                @click="changeBillingExemption(company)"
                                            />
                                            <v-list-item
                                                v-if="isSuperAdmin"
                                                density="compact"
                                                title="App Installs"
                                                :to="{name: 'admin-app-installs', query: {company_id: company.id}}"
                                            />
                                        </v-list>
                                    </v-menu>
                                </v-btn-group>
                            </div>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="11">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="11">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        v-model="currentPage"
                        @update:model-value="fetchCompanies"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <Create-Company v-if="createDialogSwitch" v-model="createDialogSwitch" @addCompany="addCompany"/>
    <Edit-Company v-if="editDialogSwitch" v-model="editDialogSwitch" :company="selectedCompany"
                  @updateCompany="updateCompany"/>
    <Status v-if="statusDialogSwitch" v-model="statusDialogSwitch" :company="selectedCompany"
            @updateStatus="updateStatus"/>
    <BillingExemption v-if="billingExemptionDialogSwitch" v-model="billingExemptionDialogSwitch"
                      :company="selectedCompany" @updateBillingExemption="updateBillingExemption"/>
    <Users v-if="usersDialogSwitch" v-model="usersDialogSwitch" :all-users="users" :company="selectedCompany"
           :roles="roles"/>
    <Locations v-if="locationsDialogSwitch" v-model="locationsDialogSwitch" :company="selectedCompany"/>
</template>

<script setup>
import {computed, onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {strOperations} from "@/composables/globals";
import {formatDateTime} from '@/composables/useTimezone';
import {useSnackbarStore} from "@/stores/snackBarStore";
import ThBtn from "@/components/globals/th-btn.vue";
import Status from "@/components/dialogs/companies/Status.vue";
import BillingExemption from "@/components/dialogs/companies/BillingExemption.vue";
import EditCompany from "@/components/dialogs/companies/Edit.vue";
import Users from "@/components/dialogs/companies/Users/Index.vue";
import CreateCompany from "@/components/dialogs/companies/Create.vue";
import Locations from "@/components/dialogs/companies/Locations/Index.vue";
import NoteIcon from "@/components/dialogs/notes/NoteIcon.vue";
import {useAuthStore} from "@/stores/authStore";

const users = ref([]);
const roles = ref([]);
const portals = ref([]);
const companies = ref([]);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const sortedType = ref(null);
const sortedColumn = ref(null);
const {showSnackbar} = useSnackbarStore();
const authStore = useAuthStore();
// Super admin sessions come back with type 'admin' and a separate super flag.
const isSuperAdmin = computed(() => !!authStore.user?.super);
const locationsDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const statusDialogSwitch = ref(false);
const billingExemptionDialogSwitch = ref(false);
const usersDialogSwitch = ref(false);
const editDialogSwitch = ref(false);
const selectedCompany = ref(null);
const formData = reactive({
    'portal': null,
    'status': null
});
const quickSearch = ref(null);

onMounted(() => {
    fetchCompanies();
    fetchPortals();
    fetchRoles();
    fetchUsers();
});

const fetchRoles = () => {
    send('get', `roles`)
        .then((response) => {
            roles.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Roles could not be loaded', 'error')
        })
}
const fetchUsers = () => {
    send('get', `users?list=true`)
        .then((response) => {
            users.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Users could not be loaded', 'error')
        })
}
const fetchPortals = () => {
    send('get', `admin/portals`)
        .then((response) => {
            portals.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Portals could not be loaded', 'error')
        })
}
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    currentPage.value = 1;
    fetchCompanies();
}
const applyQuickSearch = () => {
    currentPage.value = 1;
    fetchCompanies();
};
const fetchCompanies = () => {
    loading.value = true;
    send('get', `companies`, {
        page: currentPage.value,
        sort: sortedColumn.value,
        order: sortedType.value,
        ...formData,
        quick_search: quickSearch.value,
    })
        .then((response) => {
            companies.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Companies could not be loaded', 'error')
        })
}
const addCompany = (company) => {
    // companies.value.unshift(company);
    currentPage.value = 1;
    fetchCompanies();
}
const editCompany = (company) => {
    selectedCompany.value = company;
    editDialogSwitch.value = true;
}
const companyUsers = (company) => {
    selectedCompany.value = company;
    usersDialogSwitch.value = true;
}
const companyLocations = (company) => {
    selectedCompany.value = company;
    locationsDialogSwitch.value = true;
}
const updateCompany = (company) => {
    companies.value.map((comp, index) => {
        if (comp.id === company.id) {
            companies.value[index] = company;
        }
    });
}
const updateStatus = (company) => {
    companies.value.map((comp, index) => {
        if (comp.id === company.company_id) {
            companies.value[index]['status'] = company.status;
        }
    });
}
const changeStatus = (company) => {
    selectedCompany.value = company;
    statusDialogSwitch.value = true;
}
const changeBillingExemption = (company) => {
    selectedCompany.value = company;
    billingExemptionDialogSwitch.value = true;
}
const updateBillingExemption = (payload) => {
    companies.value.map((comp, index) => {
        if (comp.id === payload.company_id) {
            companies.value[index]['billing_exempt'] = payload.billing_exempt;
        }
    });
}
const deleteCompany = (company) => {
    if (confirm(`Are you sure to delete "${company.company_name}" company?`)) {
        send('delete', `companies/${company.id}`)
            .then((response) => {
                if (response.data.success) {
                    companies.value.map((comp, index) => {
                        if (comp.id === company.id) {
                            companies.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`"${company.company_name}" Company could not be deleted`, 'error')
            })
    }
}
</script>
